'use client';

import { useMutation } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { ComponentProps, ReactNode, createContext, useContext } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { QueryApi } from '@/features/query/api';
import { isEmailValid } from '@/shared/lib/validations';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Form } from '@/shared/ui/form';
import { Layout } from '@/shared/ui/layout';
import { Modal } from '@/shared/ui/modal';
import { ModalService } from '@/shared/ui/modal/ModalService';
import { ModalProps } from '@/shared/ui/modal/types';
import { OrderMageBranding } from '@/shared/ui/ordermage-branding';
import './auth-modal.scss';

type FormValues = {
    email: string;
};

type Props = ModalProps & {
    children?: ReactNode;
};

type ResetPasswordModalContextValue = {
    form: UseFormReturn<FormValues>;
    onClose: ModalProps['onClose'];
};

const ResetPasswordModalContext = createContext<ResetPasswordModalContextValue | null>(null);

const useResetPasswordModalContext = () => {
    const context = useContext(ResetPasswordModalContext);
    if (!context) {
        throw new Error('ResetPasswordModal components must be used within ResetPasswordModal.');
    }
    return context;
};

const ResetPasswordModalProvider = ({ children, ...props }: Props) => {
    const form = useForm<FormValues>({
        defaultValues: {
            email: '',
        },
    });

    return (
        <ResetPasswordModalContext.Provider
            value={{
                form,
                onClose: props.onClose,
            }}
        >
            {children}
        </ResetPasswordModalContext.Provider>
    );
};

/** Renders only the title text. Use inside prefab: "Modal.Title". */
const ResetPasswordModalTitle = () => {
    const t = useTranslations();
    return <>{t('RESET_PASSWORD_MODAL.TITLE')}</>;
};

type FormProps = ComponentProps<typeof Layout>;
const ResetPasswordModalForm = ({ children, media, ...props }: FormProps) => {
    const t = useTranslations();
    const { form, onClose } = useResetPasswordModalContext();
    const { mutateAsync } = useMutation(QueryApi.Customer.mutations.RequestPasswordReset);

    const handleSubmit = async (data: FormValues) => {
        const res = await mutateAsync(data.email);
        if (res.result === 'OK') {
            toast(({ closeToast }) => (
                <Alert
                    heading={t('RESET_PASSWORD_MODAL.NOTIFICATIONS.SUCCESS')}
                    type="info"
                    onClose={closeToast}
                />
            ));
            onClose();
            return;
        }
        toast(({ closeToast }) => (
            <Alert
                heading={t('RESET_PASSWORD_MODAL.NOTIFICATIONS.ERROR')}
                type="error"
                onClose={closeToast}
            />
        ));
    };

    return (
        <Form {...form} noValidate onSubmit={form.handleSubmit(handleSubmit)} className="auth-modal__form">
            <Layout media={media} {...props}>
                {children}
            </Layout>
        </Form>
    );
};

const ResetPasswordModalEmailField = () => {
    const t = useTranslations();
    return (
        <Form.TextField
            data-testid="reset-password-modal-email-field"
            name="email"
            label="E-mail"
            type="email"
            rules={{
                required: true,
                validate: (value) => isEmailValid(value) || t('ERRORS.INVALID_EMAIL'),
            }}
        />
    );
};

const ResetPasswordModalSubmitButton = () => {
    const t = useTranslations();
    return (
        <Button data-testid="reset-password-modal-submit-button" className="auth-modal__button" type="submit">
            {t('RESET_PASSWORD_MODAL.SUBMIT_BUTTON')}
        </Button>
    );
};

const ResetPasswordModalBottomPanel = () => {
    const handleShowTerms = () => {
        ModalService.showPrefab('TermsAndConditionsModal', {});
    };
    return <OrderMageBranding onLogoClick={handleShowTerms} />;
};

export const ResetPasswordModalWrapper = ({ children, ...props }: Props) => {
    return (
        <ResetPasswordModalProvider {...props}>
            <Modal
                {...props}
                wrapperProps={{
                    className: 'auth-modal',
                    containerProps: {
                        className: 'auth-modal__container',
                    },
                    'data-testid': 'reset-password-modal',
                }}
            >
                {children}
            </Modal>
        </ResetPasswordModalProvider>
    );
};

/** Provider that only provides context and renders children (Modal prefab). */
export const ResetPasswordModalProviderComponent = ({ children, ...props }: Props) => (
    <ResetPasswordModalProvider {...props}>{children}</ResetPasswordModalProvider>
);

export {
    ResetPasswordModalWrapper as Wrapper,
    ResetPasswordModalProviderComponent as Provider,
    ResetPasswordModalTitle as Title,
    ResetPasswordModalForm as Form,
    ResetPasswordModalEmailField as EmailField,
    ResetPasswordModalSubmitButton as SubmitButton,
    ResetPasswordModalBottomPanel as BottomPanel,
};

export { ResetPasswordModalWrapper as ResetPasswordModal };
